import React, {useState, useEffect} from "react";
import {useParams} from "react-router-dom";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import ButtonPrimary from "../../../ui/shared/Button/ButtonPrimary";
import { useForm, FormProvider} from "react-hook-form";
import {TransferAlert} from "./TransferAlert";
import TransferRequestTransportDetails from "./TransferRequestTransportDetails";
import TransferRequestPersonalsDetails from "./TransferRequestPersonalsDetails";
import TransferRequestFooterDetails from "./TransferRequestFooterDetails";
import Loader from "../../../ui/shared/Loader/Loader";
import TransferRequestHeaderDetails from "./TransferRequestHeaderDetails";
import useCustomerContext from "../../../customers/hooks/useCustomerContext";
import useTransferRequestData from "../../hooks/useTransferRequestData";
import {useOidcClientContext} from "../../../ui/context/OidcClientContext";
import useProfileGet from "../../../profile/hooks/useProfileGet";
import useTransferSubmitHandler from "../../hooks/useTransferSubmitHandler";
import BannerImage from "../../../common/components/BannerImage";
import {Alert} from "../../../ui/shared/Alert/Alert";
import LoginOrRegisterMsg from "../../../ui/components/LoginOrRegister/LoginOrRegisterMsg";


const TransferRequestView = ({ showBannerOnly = false, showFormOnly = false }) => {
    const {authenticated} = useOidcClientContext();
    const {data: profile} = useProfileGet({execute: authenticated});
    const t = useTranslate();
    const {eventCityId, departmentId} = useParams();
    const customer = useCustomerContext();
    const form = useForm({mode: "onBlur"});
    const {handleSubmit, setValue, setError, reset} = form;
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<"default" | "warning" | "info" | "success" | "error">("info");
    const [showForm, setShowForm] = useState<boolean>(true);

    const {
        eventCity,
        event,
        city,
        department,
        isLoading,
        isEventCityLoading,
        isEventLoading,
        isDepartmentLoading,
    } = useTransferRequestData(eventCityId, departmentId);

    // Restore default values
    const restoreDefaultValues = () => {
        // Restore event-related values
        if (eventCity) { setValue("eventCity", eventCity.id); }
        if (event) { setValue("event", event.id); }
        if (city) { setValue("city", city.id); }
        if (department) { setValue("department", departmentId); }
        
        // Restore profile-related values
        if (profile) {
            setValue("passengerName", profile?.firstName);
            setValue("passengerLastName", profile?.lastName);
            setValue("passengerContactEmail", profile?.email);
            setValue("passengerPhoneNumber", profile?.phoneNumber);
            setValue("invoiceFullName", profile?.invoiceFullName);
            setValue("invoiceAddress", profile?.invoiceAddress);
            setValue("nationality", profile?.nationality);
            setValue("documentType", profile?.documentType);
            setValue("invoicePassportCif", profile?.documentNumber);
            setValue("dialCode", profile?.dialCode ? `${profile.iso2}-${profile.dialCode}` : "");
        }
    };

    // Handle new transfer request
    const handleNewTransfer = () => {
        reset();
        restoreDefaultValues();
        setShowForm(true);
        setShowAlert(false);
    };

    // Hook para manejar el submit del formulario
    const onSubmit = useTransferSubmitHandler({
        customer, 
        setShowAlert, 
        setAlertMessage, 
        setAlertType, 
        reset,
        setValue,
        setError,
        eventCity,
        event,
        city,
        department,
        profile,
        setShowForm,
    });

    // Setear los campos derivados del hook
    useEffect(() => {
        if (eventCity) { setValue("eventCity", eventCity.id); }
        if (event) { setValue("event", event.id); }
        if (city) { setValue("city", city.id); }
        if (department) { setValue("department", departmentId); }
    }, [eventCity, event, department, city, setValue]);

    useEffect(() => {
        if (authenticated && profile) {
            setValue("passengerName", profile?.firstName);
            setValue("passengerLastName", profile?.lastName);
            setValue("passengerContactEmail", profile?.email);
            setValue("passengerPhoneNumber", profile?.phoneNumber);
            setValue("invoiceFullName", profile?.invoiceFullName);
            setValue("invoiceAddress", profile?.invoiceAddress);
            setValue("nationality", profile?.nationality);
            setValue("documentType", profile?.documentType);
            setValue("invoicePassportCif", profile?.documentNumber);
            setValue("dialCode", profile?.dialCode ? `${profile.iso2}-${profile.dialCode}` : "");
        }
    }, [authenticated, profile, setValue]);


    return (
        <div className="nc-TransferRequestView flex flex-col relative w-full">
            {/* Banner Image Component */}
            {!showFormOnly && (department?.departmentImageContentUrl || eventCity?.bannerContentUrl) && (
                <BannerImage 
                    mainImage={department?.departmentImageContentUrl || eventCity?.bannerContentUrl || ""} 
                />
            )}

            {/* Form Container with negative margin overlap */}
            {!showBannerOnly && (
                <div className="flex justify-center mb-12 -mt-6 lg:-mt-20 w-full px-4">
                    <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-2 px-0 sm:p-6 xl:p-8 bg-white dark:bg-neutral-900">
                        {eventCity && event && (
                            <TransferRequestHeaderDetails
                                cityEvent={eventCity}
                                event={event}
                                city={city}
                                customer={customer}
                                isPendingEvent={isEventLoading}
                                isPendingCity={isEventCityLoading}
                                isPendingDepartment={isDepartmentLoading}
                            />
                        )}
                    {showAlert && (
                        <div className="flex flex-col gap-4 items-center">
                            <TransferAlert
                                className="mt-12"
                                type={alertType}
                                closable={true}
                                onClose={() => setShowAlert(false)}
                            >
                                {alertMessage}
                            </TransferAlert>
                            {alertType === 'success' && (
                                <ButtonPrimary onClick={handleNewTransfer} className="mt-6">
                                    {t("transfers.message.newTransferRequest")}
                                </ButtonPrimary>
                            )}
                        </div>
                    )}
                        
                        {isLoading && (
                            <div className="flex justify-center py-8">
                                <Loader />
                            </div>
                        )}
                        
                        {!isLoading && (!eventCityId || !departmentId) && (
                            <Alert type="error" closable={false}>
                                {t("common.error")} - {t("common.invalidParameters")}
                            </Alert>
                        )}
                        
                        {!isLoading && eventCity && department && showForm && (
                            <div>
                                <div className="mt-6">
                                    <FormProvider {...form}>
                                        <form onSubmit={handleSubmit(onSubmit)}>
                                            <div className="border-b border-neutral-200 dark:border-neutral-700 my-5"></div>
                                            {/* Componente TransferRequestTransportDetails */}
                                            <LoginOrRegisterMsg />
                                            <TransferRequestTransportDetails department={department} eventCity={eventCity}/>
                                            <TransferRequestPersonalsDetails department={department} profile={profile} />
                                            <TransferRequestFooterDetails/>

                                            <div className="pt-6 flex justify-center">
                                                <ButtonPrimary type="submit">
                                                    {t("common.actions.send")}
                                                </ButtonPrimary>
                                            </div>
                                        </form>
                                    </FormProvider>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default TransferRequestView;
